import classNames from "classnames";
import { Immutable } from "immer";
import React, { ReactNode, memo, useCallback, useState } from "react";

import { CompatibilityAccordionModule } from "@reactivated";

import { useMountEffect } from "../../hooks/useMountEffect";
import { BreakPoint } from "../../models/screen";
import { getViewportBreakpoint } from "../../utils/detectMobile";
import { Image } from "../Image";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

type ICompatibilityPanel =
    CompatibilityAccordionModule["compatibility_panels"][number];

type IProps = Immutable<{
    value: CompatibilityAccordionModule;
}>;

type ICompatibilityAccordionPanelProps = Immutable<{
    panel: ICompatibilityPanel;
    panelIndex: number;
    useIcon: boolean;
    cms: CompatibilityAccordionModule;
    isActive: boolean;
    onPanelClick: (panelIndex: number) => void;
    isMobile: boolean;
}>;

type ICompatibilityAccordionPanelBodyProps = Immutable<{
    description: string | null;
    imageUrl?: string;
    title: string;
    isMobile: boolean;
}>;

function getIcon(
    useIcon: boolean,
    cms: Immutable<CompatibilityAccordionModule>,
    compatibility: boolean,
): React.JSX.Element | undefined {
    if (!useIcon) {
        return;
    }
    const imgName = compatibility ? "icon_comp" : "icon_incomp";
    const altText = compatibility ? "compatible" : "incompatibile";

    return <Image src={cms[`${imgName}`]?.url} alt={`${altText} icon`} />;
}

const CompatibilityAccordionPanelBody = memo(
    ({
        description,
        imageUrl,
        title,
        isMobile,
    }: ICompatibilityAccordionPanelBodyProps): React.JSX.Element => {
        return (
            <>
                <RichText html={description || ""} />

                {isMobile && imageUrl && (
                    <Image
                        src={imageUrl}
                        alt={title}
                        lazy={false}
                        className=""
                    />
                )}
            </>
        );
    },
);

const CompatibilityAccordionPanel = memo(
    ({
        panel,
        panelIndex,
        useIcon,
        cms,
        isActive,
        onPanelClick,
        isMobile,
    }: ICompatibilityAccordionPanelProps): React.JSX.Element => {
        const rowClassName = classNames({
            [styles.row]: true,
            [styles.rowIcon]: useIcon,
        });
        const triggerClassName = classNames({
            [styles.accordionPanelTrigger]: true,
            [styles.accordionPanelTriggerIsActive]: isActive,
        });

        const targetClassName = classNames({
            [styles.accordionPanelTarget]: true,
            [styles.compatibilityAccordionImage]: isMobile,
            [styles.accordionPanelTargetIsActive]: isActive,
        });
        return (
            <div className={rowClassName} key={panel.value.title}>
                <h3>
                    <button
                        className={triggerClassName}
                        onClick={() => {
                            onPanelClick(panelIndex);
                        }}
                        id={`trigger_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
                        aria-controls={`panel_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
                        aria-expanded={isActive}
                    >
                        {getIcon(useIcon, cms, panel.value.choice)}{" "}
                        {panel.value.title}
                    </button>
                </h3>
                <div
                    className={targetClassName}
                    id={`panel_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
                >
                    {isActive && (
                        <CompatibilityAccordionPanelBody
                            description={panel.value.description}
                            imageUrl={panel.value.image?.url}
                            title={panel.value.title}
                            isMobile={isMobile}
                        />
                    )}
                </div>
            </div>
        );
    },
);

export const CompatibilityAccordion = (props: IProps): ReactNode => {
    const [activePanelIndex, setActivePanelIndex] = useState<number | null>(
        null,
    );
    const [isMounted, setIsMounted] = useState(false);

    const cms = props.value;

    useMountEffect(() => {
        setActivePanelIndex(cms.compatibility_panels.length > 0 ? 0 : null);
        setIsMounted(true);
    });

    // Breakpoint is checked after mount to prevent hydration mismatches
    const viewportBreakpoint = isMounted ? getViewportBreakpoint() : null;
    const isDesktop =
        viewportBreakpoint !== null && viewportBreakpoint >= BreakPoint.MEDIUM;
    const isMobile =
        viewportBreakpoint !== null && viewportBreakpoint < BreakPoint.MEDIUM;
    const activePanel =
        activePanelIndex !== null
            ? cms.compatibility_panels[activePanelIndex]
            : null;

    const handlePanelClick = useCallback((panelIndex: number) => {
        setActivePanelIndex((current) =>
            current === panelIndex ? null : panelIndex,
        );
    }, []);

    return (
        <div className={styles.compatibilityAccordionContent}>
            <RichText
                html={cms.compatibility_header || ""}
                className={styles.compatibilityAccordionHeader}
            />
            {isDesktop && (
                <div
                    className={`${styles.compatibilityAccordionImage} ${styles.accordionPanelTarget} ${styles.accordionPanelTargetIsActive}`}
                >
                    {activePanel && (
                        <Image
                            lazy={false}
                            className=""
                            src={activePanel?.value.image?.url}
                            alt={activePanel?.value.title}
                        />
                    )}
                </div>
            )}
            <div className={styles.compatibilityAccordionPanels}>
                {cms.compatibility_panels.map(
                    (
                        panel: Immutable<ICompatibilityPanel>,
                        panelIndex: number,
                    ) => (
                        <CompatibilityAccordionPanel
                            key={`panel-trigger-${panelIndex}`}
                            panel={panel}
                            panelIndex={panelIndex}
                            useIcon={cms.use_icon}
                            cms={cms}
                            isActive={activePanelIndex === panelIndex}
                            onPanelClick={handlePanelClick}
                            isMobile={isMobile}
                        />
                    ),
                )}
            </div>
            <RichText html={cms.compatibility_footer || ""} />
        </div>
    );
};
